import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { formatINR } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Users, 
  Sparkles, 
  Utensils, 
  TrendingUp,
  Upload,
  FileSpreadsheet,
  Youtube,
  Instagram,
  Save,
  ExternalLink,
  BarChart3,
  PieChart,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  
  // YouTube videos state
  const [youtubeVideos, setYoutubeVideos] = useState([
    { id: "", title: "Video 1", url: "" },
    { id: "", title: "Video 2", url: "" },
    { id: "", title: "Video 3", url: "" }
  ]);
  const [savingVideos, setSavingVideos] = useState(false);

  // Instagram reels state
  const [instagramReels, setInstagramReels] = useState([
    { id: "", title: "Reel 1", url: "" },
    { id: "", title: "Reel 2", url: "" },
    { id: "", title: "Reel 3", url: "" }
  ]);
  const [savingReels, setSavingReels] = useState(false);

  // Chart data state
  const [monthlyPerformance, setMonthlyPerformance] = useState([]);
  const [revenueCategories, setRevenueCategories] = useState([]);
  const [savingCharts, setSavingCharts] = useState(false);
  const [showCharts, setShowCharts] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchYouTubeVideos();
    fetchInstagramReels();
    fetchChartData();
    fetchChartSettings();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchYouTubeVideos = async () => {
    try {
      const response = await axios.get(`${API}/youtube-videos`);
      if (response.data && response.data.length > 0) {
        const videos = response.data.slice(0, 3).map((v, idx) => ({
          id: v.id,
          title: v.title,
          url: `https://www.youtube.com/watch?v=${v.id}`
        }));
        // Fill remaining slots if less than 3
        while (videos.length < 3) {
          videos.push({ id: "", title: `Video ${videos.length + 1}`, url: "" });
        }
        setYoutubeVideos(videos);
      }
    } catch (error) {
      console.error("Failed to fetch YouTube videos:", error);
    }
  };

  // Extract YouTube video ID from various URL formats
  const extractVideoId = (url) => {
    if (!url) return "";
    
    // Already just an ID (11 characters)
    if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) {
      return url.trim();
    }
    
    // Standard YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return "";
  };

  const handleVideoUrlChange = (index, url) => {
    const videoId = extractVideoId(url);
    const updatedVideos = [...youtubeVideos];
    updatedVideos[index] = {
      ...updatedVideos[index],
      url: url,
      id: videoId
    };
    setYoutubeVideos(updatedVideos);
  };

  const handleVideoTitleChange = (index, title) => {
    const updatedVideos = [...youtubeVideos];
    updatedVideos[index] = {
      ...updatedVideos[index],
      title: title
    };
    setYoutubeVideos(updatedVideos);
  };

  const saveYouTubeVideos = async () => {
    setSavingVideos(true);
    try {
      // First, clear existing videos via a bulk update endpoint
      await axios.post(`${API}/youtube-videos/bulk-update`, {
        videos: youtubeVideos
          .filter(v => v.id) // Only save videos with valid IDs
          .map((v, idx) => ({
            id: v.id,
            title: v.title || `KAI Video ${idx + 1}`,
            thumbnail: `https://img.youtube.com/vi/${v.id}/mqdefault.jpg`
          }))
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success("YouTube videos updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save videos");
    } finally {
      setSavingVideos(false);
    }
  };

  // Instagram functions
  const fetchInstagramReels = async () => {
    try {
      const response = await axios.get(`${API}/instagram-reels`);
      if (response.data && response.data.length > 0) {
        const reels = response.data.slice(0, 3).map((r, idx) => ({
          id: r.id,
          title: r.title,
          url: r.url
        }));
        // Fill remaining slots if less than 3
        while (reels.length < 3) {
          reels.push({ id: "", title: `Reel ${reels.length + 1}`, url: "" });
        }
        setInstagramReels(reels);
      }
    } catch (error) {
      console.error("Failed to fetch Instagram reels:", error);
    }
  };

  // Extract Instagram post ID from URL
  const extractInstagramId = (url) => {
    if (!url) return "";
    
    // Match Instagram post/reel URLs
    const patterns = [
      /instagram\.com\/p\/([a-zA-Z0-9_-]+)/,
      /instagram\.com\/reel\/([a-zA-Z0-9_-]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    // If it looks like just an ID
    if (/^[a-zA-Z0-9_-]+$/.test(url.trim()) && url.trim().length > 5) {
      return url.trim();
    }
    
    return "";
  };

  const handleReelUrlChange = (index, url) => {
    const reelId = extractInstagramId(url);
    const updatedReels = [...instagramReels];
    updatedReels[index] = {
      ...updatedReels[index],
      url: url,
      id: reelId
    };
    setInstagramReels(updatedReels);
  };

  const handleReelTitleChange = (index, title) => {
    const updatedReels = [...instagramReels];
    updatedReels[index] = {
      ...updatedReels[index],
      title: title
    };
    setInstagramReels(updatedReels);
  };

  const saveInstagramReels = async () => {
    setSavingReels(true);
    try {
      await axios.post(`${API}/instagram-reels/bulk-update`, {
        reels: instagramReels
          .filter(r => r.id) // Only save reels with valid IDs
          .map((r, idx) => ({
            id: r.id,
            title: r.title || `KAI Reel ${idx + 1}`,
            url: r.url || `https://www.instagram.com/p/${r.id}/`
          }))
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success("Instagram reels updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reels");
    } finally {
      setSavingReels(false);
    }
  };

  // Chart data functions
  const fetchChartData = async () => {
    try {
      const [perfRes, revRes] = await Promise.all([
        axios.get(`${API}/monthly-performance`),
        axios.get(`${API}/revenue-categories`)
      ]);
      if (perfRes.data && perfRes.data.length > 0) {
        setMonthlyPerformance(perfRes.data);
      } else {
        setMonthlyPerformance([{ month: '', amount: '' }]);
      }
      if (revRes.data && revRes.data.length > 0) {
        setRevenueCategories(revRes.data);
      } else {
        setRevenueCategories([{ category: '', amount: '' }]);
      }
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
      setMonthlyPerformance([{ month: '', amount: '' }]);
      setRevenueCategories([{ category: '', amount: '' }]);
    }
  };

  const fetchChartSettings = async () => {
    try {
      const response = await axios.get(`${API}/chart-settings`);
      setShowCharts(response.data.show_charts ?? true);
    } catch (error) {
      console.error("Failed to fetch chart settings:", error);
    }
  };

  const toggleChartsVisibility = async () => {
    const newValue = !showCharts;
    setShowCharts(newValue);
    try {
      await axios.post(`${API}/chart-settings?show_charts=${newValue}`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(newValue ? "Charts are now visible on the public page" : "Charts are now hidden from the public page");
    } catch (error) {
      setShowCharts(!newValue); // Revert on error
      toast.error("Failed to update chart visibility");
    }
  };

  const addMonthlyEntry = () => {
    setMonthlyPerformance([{ month: '', amount: '' }, ...monthlyPerformance]);
  };

  const removeMonthlyEntry = (index) => {
    setMonthlyPerformance(monthlyPerformance.filter((_, i) => i !== index));
  };

  const updateMonthlyEntry = (index, field, value) => {
    const updated = [...monthlyPerformance];
    updated[index] = { ...updated[index], [field]: value };
    setMonthlyPerformance(updated);
  };

  const moveMonthlyEntry = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= monthlyPerformance.length) return;
    
    const updated = [...monthlyPerformance];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    setMonthlyPerformance(updated);
  };

  const addRevenueEntry = () => {
    setRevenueCategories([{ category: '', amount: '' }, ...revenueCategories]);
  };

  const removeRevenueEntry = (index) => {
    setRevenueCategories(revenueCategories.filter((_, i) => i !== index));
  };

  const updateRevenueEntry = (index, field, value) => {
    const updated = [...revenueCategories];
    updated[index] = { ...updated[index], [field]: value };
    setRevenueCategories(updated);
  };

  const moveRevenueEntry = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= revenueCategories.length) return;
    
    const updated = [...revenueCategories];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    setRevenueCategories(updated);
  };

  const saveChartData = async () => {
    setSavingCharts(true);
    try {
      await Promise.all([
        axios.post(`${API}/monthly-performance/bulk-update`, 
          monthlyPerformance.filter(e => e.month && e.amount).map(e => ({
            ...e,
            amount: parseFloat(e.amount) || 0
          })),
          { headers: { Authorization: `Bearer ${getToken()}` } }
        ),
        axios.post(`${API}/revenue-categories/bulk-update`,
          revenueCategories.filter(e => e.category && e.amount).map(e => ({
            ...e,
            amount: parseFloat(e.amount) || 0
          })),
          { headers: { Authorization: `Bearer ${getToken()}` } }
        )
      ]);
      toast.success("Chart data updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save chart data");
    } finally {
      setSavingCharts(false);
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-excel`, formData, {
        headers: { 
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const statCards = [
    { 
      label: "Total Leads", 
      value: stats?.leads_count || 0, 
      icon: Users,
      color: "bg-blue-500"
    },
    { 
      label: "Decor Items", 
      value: stats?.decor_items_count || 0, 
      icon: Sparkles,
      color: "bg-purple-500"
    },
    { 
      label: "Food Tiers", 
      value: stats?.food_tiers_count || 0, 
      icon: Utensils,
      color: "bg-green-500"
    },
    { 
      label: "Avg. Budget", 
      value: stats?.avg_budget ? formatINR(stats.avg_budget) : "—", 
      icon: TrendingUp,
      color: "bg-amber-500"
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div 
                key={idx}
                className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#2D2D2D]">
                      {loading ? "—" : stat.value}
                    </p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <div className="flex items-center gap-3 mb-4">
            <FileSpreadsheet className="w-6 h-6 text-[#B08968]" />
            <h2 className="text-xl font-serif font-medium">Import from Excel</h2>
          </div>
          
          <p className="text-muted-foreground mb-4">
            Upload your pricing spreadsheet to update venue, food, and decor data.
            Existing data will be replaced.
          </p>
          
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelImport}
              disabled={importing}
              className="max-w-xs"
              data-testid="excel-import-input"
            />
            {importing && (
              <span className="text-sm text-muted-foreground">Importing...</span>
            )}
          </div>
        </div>

        {/* YouTube Videos Management */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Youtube className="w-6 h-6 text-red-600" />
              <h2 className="text-xl font-serif font-medium">YouTube Videos</h2>
            </div>
            <Button
              onClick={saveYouTubeVideos}
              disabled={savingVideos}
              className="bg-[#B08968] hover:bg-[#9A7558] text-white"
              data-testid="save-youtube-btn"
            >
              {savingVideos ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Videos
                </>
              )}
            </Button>
          </div>
          
          <p className="text-muted-foreground mb-6">
            Paste YouTube video links below. These videos will appear in the "See KAI in action" section on the public estimator page.
          </p>
          
          <div className="space-y-4">
            {youtubeVideos.map((video, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-secondary/30 rounded-xl">
                {/* Thumbnail Preview - Portrait format */}
                <div className="md:col-span-1">
                  {video.id ? (
                    <div className="aspect-[3/4] w-16 rounded-lg overflow-hidden bg-gray-200">
                      <img
                        src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[3/4] w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                      <Youtube className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Title Input */}
                <div className="md:col-span-3">
                  <Label className="text-xs text-muted-foreground mb-1 block">Video Title</Label>
                  <Input
                    value={video.title}
                    onChange={(e) => handleVideoTitleChange(index, e.target.value)}
                    placeholder={`Video ${index + 1} Title`}
                    data-testid={`video-title-${index}`}
                  />
                </div>
                
                {/* URL Input */}
                <div className="md:col-span-7">
                  <Label className="text-xs text-muted-foreground mb-1 block">YouTube URL or Video ID</Label>
                  <Input
                    value={video.url}
                    onChange={(e) => handleVideoUrlChange(index, e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or video ID"
                    data-testid={`video-url-${index}`}
                  />
                </div>
                
                {/* Preview Link */}
                <div className="md:col-span-1 flex items-end">
                  {video.id && (
                    <a
                      href={`https://www.youtube.com/watch?v=${video.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-red-600 transition-colors"
                      title="Preview on YouTube"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Tip: You can paste full YouTube URLs (youtube.com/watch?v=..., youtu.be/...) or just the video ID.
          </p>
        </div>

        {/* Instagram Reels Management */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Instagram className="w-6 h-6 text-pink-600" />
              <h2 className="text-xl font-serif font-medium">Instagram Reels</h2>
            </div>
            <Button
              onClick={saveInstagramReels}
              disabled={savingReels}
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 text-white"
              data-testid="save-instagram-btn"
            >
              {savingReels ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Reels
                </>
              )}
            </Button>
          </div>
          
          <p className="text-muted-foreground mb-6">
            Paste Instagram reel/post links below. These will appear in the "Follow us on Instagram" section on the public page.
          </p>
          
          <div className="space-y-4">
            {instagramReels.map((reel, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50 rounded-xl">
                {/* Thumbnail Preview - Portrait format */}
                <div className="md:col-span-1">
                  {reel.id ? (
                    <div className="aspect-[3/4] w-16 rounded-lg overflow-hidden bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                  ) : (
                    <div className="aspect-[3/4] w-16 rounded-lg bg-gray-200 flex items-center justify-center">
                      <Instagram className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Title Input */}
                <div className="md:col-span-3">
                  <Label className="text-xs text-muted-foreground mb-1 block">Reel Title</Label>
                  <Input
                    value={reel.title}
                    onChange={(e) => handleReelTitleChange(index, e.target.value)}
                    placeholder={`Reel ${index + 1} Title`}
                    data-testid={`reel-title-${index}`}
                  />
                </div>
                
                {/* URL Input */}
                <div className="md:col-span-7">
                  <Label className="text-xs text-muted-foreground mb-1 block">Instagram URL</Label>
                  <Input
                    value={reel.url}
                    onChange={(e) => handleReelUrlChange(index, e.target.value)}
                    placeholder="https://www.instagram.com/p/... or /reel/..."
                    data-testid={`reel-url-${index}`}
                  />
                </div>
                
                {/* Preview Link */}
                <div className="md:col-span-1 flex items-end">
                  {reel.id && (
                    <a
                      href={reel.url || `https://www.instagram.com/p/${reel.id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-white rounded-lg text-muted-foreground hover:text-pink-600 transition-colors"
                      title="Preview on Instagram"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Tip: You can paste full Instagram URLs (instagram.com/p/... or instagram.com/reel/...).
          </p>
        </div>

        {/* Performance & Revenue Charts Management */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-[#B08968]" />
              <h2 className="text-xl font-serif font-medium">Performance & Revenue Charts</h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="show-charts" className="text-sm text-muted-foreground">
                  {showCharts ? "Visible" : "Hidden"}
                </Label>
                <Switch
                  id="show-charts"
                  checked={showCharts}
                  onCheckedChange={toggleChartsVisibility}
                  data-testid="charts-visibility-toggle"
                />
              </div>
              <Button
                onClick={saveChartData}
                disabled={savingCharts}
                className="bg-[#B08968] hover:bg-[#9A7558] text-white"
                data-testid="save-charts-btn"
              >
                {savingCharts ? "Saving..." : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Chart Data
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <p className="text-muted-foreground mb-6">
            Manage the data displayed in the "Performance & Revenue Mix at KAI" section on the public page. Use the toggle to show or hide the entire section.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Performance */}
            <div className="bg-secondary/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#B08968]" />
                  <h3 className="font-medium">Monthly Performance</h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addMonthlyEntry}
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Month
                </Button>
              </div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {monthlyPerformance.map((entry, index) => (
                  <div key={index} className="flex gap-2 items-center bg-white p-3 rounded-lg">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveMonthlyEntry(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-[#B08968] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveMonthlyEntry(index, 'down')}
                        disabled={index === monthlyPerformance.length - 1}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-[#B08968] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      value={entry.month || ''}
                      onChange={(e) => updateMonthlyEntry(index, 'month', e.target.value)}
                      placeholder="e.g., August 2024"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={entry.amount || ''}
                      onChange={(e) => updateMonthlyEntry(index, 'amount', e.target.value)}
                      placeholder="Amount (₹)"
                      className="w-32"
                    />
                    <button
                      onClick={() => removeMonthlyEntry(index)}
                      className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground mt-3">
                Enter month name and revenue amount in INR (e.g., 1250000 for ₹12.5L)
              </p>
            </div>

            {/* Revenue Categories */}
            <div className="bg-secondary/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#B08968]" />
                  <h3 className="font-medium">Revenue Categories</h3>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addRevenueEntry}
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Category
                </Button>
              </div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {revenueCategories.map((entry, index) => (
                  <div key={index} className="flex gap-2 items-center bg-white p-3 rounded-lg">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveRevenueEntry(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-[#B08968] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveRevenueEntry(index, 'down')}
                        disabled={index === revenueCategories.length - 1}
                        className="p-1 hover:bg-secondary rounded text-muted-foreground hover:text-[#B08968] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      value={entry.category || ''}
                      onChange={(e) => updateRevenueEntry(index, 'category', e.target.value)}
                      placeholder="e.g., Weddings"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      value={entry.amount || ''}
                      onChange={(e) => updateRevenueEntry(index, 'amount', e.target.value)}
                      placeholder="Amount (₹)"
                      className="w-32"
                    />
                    <button
                      onClick={() => removeRevenueEntry(index)}
                      className="p-2 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground mt-3">
                Enter category name and total revenue in INR. Percentages are calculated automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-border/40 p-6 shadow-elegant">
          <h2 className="text-xl font-serif font-medium mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/venue-pricing'}
              data-testid="quick-venue-btn"
            >
              <Upload className="w-5 h-5" />
              <span>Edit Venue Pricing</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/food-pricing'}
              data-testid="quick-food-btn"
            >
              <Utensils className="w-5 h-5" />
              <span>Manage Food Tiers</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/decor-catalog'}
              data-testid="quick-decor-btn"
            >
              <Sparkles className="w-5 h-5" />
              <span>Decor Catalog</span>
            </Button>
            
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/leads'}
              data-testid="quick-leads-btn"
            >
              <Users className="w-5 h-5" />
              <span>View Leads</span>
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
